// One event of a record's «История» (R-U22): who changed which field from what to what, a letter about the record, the form answer that
// made it. The line's own tools (a revision's compare and restore) come as children.

import type { ReactNode } from 'react';
import { formatPlain, type RecordObj, avatarUrl } from '@confdeck/core-shared';
import { ago } from '../fields/format.ts';
import { navigate } from '../shell/router.ts';
import { useMeta } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Avatar } from '../ui/primitives.tsx';
import s from './record.module.css';

export interface HistoryEntry { syncId: number; op: string; command: string; reason?: string; actorId: string | null; source: string; at: string; data: Record<string, unknown> | null; before: Record<string, unknown> | null; kind: string }

const COMMAND_TEXT: Record<string, string> = {
  'record.create': 'создал(а) запись', 'record.import': 'импортировал(а) запись', 'record.archive': 'перенёс(ла) в архив', 'record.restore': 'вернул(а) из архива',
  'revision.restore': 'вернул(а) ревизию', 'content.restore': 'вернул(а) версию в черновик', 'content.publish': 'опубликовал(а)',
};

export function HistoryEvent({ e, rec, children }: { e: HistoryEntry; rec: RecordObj | undefined; children?: ReactNode }) {
  const meta = useMeta();
  if (e.kind === 'letter' || e.kind === 'response') {
    const d = e.data ?? {};
    const status = e.kind === 'letter' ? meta.term(String(d.status ?? ''))?.name : null;
    return (
      <li className={s.hItem}>
        <Icon name={e.kind === 'letter' ? 'mail' : 'form'} size={16} />
        <div className={s.hBody}>
          <div>{e.kind === 'letter' ? <>Письмо <b>«{String(d.subject ?? '')}»</b> → {String(d.to ?? '')}</> : <>Ответ формы <b>«{String(d.form ?? '')}»</b></>} <span className={s.hTime}>{ago(e.at)}</span></div>
          {status && <div className={s.hLine}>{status}</div>}
          <div className={s.hLine}><a className={s.hLink} href={`/r/${String(d.id)}`} onClick={(ev) => { ev.preventDefault(); navigate(`/r/${String(d.id)}`); }}>Открыть</a></div>
        </div>
      </li>
    );
  }
  const u = meta.user(e.actorId);
  const lines: string[] = [];
  if (e.kind === 'record' && e.op === 'patch' && e.data?.data && typeof e.data.data === 'object') {
    for (const [k, v] of Object.entries(e.data.data as Record<string, unknown>)) {
      const f = rec ? meta.field(rec.typeId, k) : undefined;
      if (!f) continue;
      const was = (e.before?.data as Record<string, unknown> | undefined)?.[k];
      lines.push(`${f.name}: ${formatPlain(f, was, meta) || 'пусто'} → ${formatPlain(f, v, meta) || 'пусто'}`);
    }
  }
  if (e.kind === 'record' && e.op === 'patch' && e.data && 'ownerId' in e.data) lines.push(`Ответственный: ${meta.user(e.before?.ownerId as string)?.name ?? 'нет'} → ${meta.user(e.data.ownerId as string)?.name ?? 'нет'}`);
  if (e.kind === 'link') lines.push(e.op === 'put' ? 'добавил(а) связь' : 'убрал(а) связь');
  const what = COMMAND_TEXT[e.command] ?? (lines.length ? '' : 'изменил(а) запись');
  return (
    <li className={s.hItem}>
      {u ? <Avatar name={u.name} color={u.color} src={avatarUrl(u)} size={20} /> : <span className={s.hDot} />}
      <div className={s.hBody}>
        <div><b>{u?.name ?? (e.source === 'automation' ? 'Автоматизация' : 'Система')}</b> {what} <span className={s.hTime}>{ago(e.at)}</span></div>
        {lines.map((l, i) => <div key={i} className={s.hLine}>{l}</div>)}
        {e.reason && <div className={s.hLine}>Причина: {e.reason}</div>}
        {children}
      </div>
    </li>
  );
}
